import type {
  IGenerateSeriesPlaylistParams,
  SeriesPlaylistRecalculate,
} from '@setplex/tria-api'
import type { ICWatching } from '../continue-watching'

import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { persist } from 'effector-storage/local'
import { model as i18n } from '~/core/i18n'
import { model as router } from '~/core/router'
import { model as showsModel } from '~/entities/shows'
import { noop } from '~/shared/effector'
import { exists, nay } from '~/shared/helpers'
import { initPlayerPageShowPlaying } from '../model'
import { playlistFormatter } from './lib'

export const open = createEvent()
export const close = createEvent()
export const toggle = createEvent()
export const playEpisodeClicked = createEvent<{
  seasonId?: number
  episodeId: number
}>()

const getSeriesPlaylistKey = ({
  tvShowId,
  seasonId,
  episodeId,
  categoryId,
  showIndex,
}: {
  tvShowId: number
  seasonId?: number | null
  episodeId?: number | null
  categoryId?: number | null
  showIndex?: number | null
}) =>
  [tvShowId, seasonId ?? '', episodeId ?? '', categoryId ?? '', showIndex ?? '']
    .map(String)
    .join(':')

const $seriesPlaylistRequestKey = createStore<string | null>(null)
  .on(api.tvshows.generateSeriesPlaylistFx, (_, params) =>
    getSeriesPlaylistKey(params)
  )
  .reset(api.tvshows.generateSeriesPlaylistFx.finally)

export const $isOpened = createStore<boolean>(false)
  .on([close, router.$pathname, initPlayerPageShowPlaying], () => false)
  .on(open, () => true)
  .on(toggle, (state) => !state)

// Close Episode List as soon as an episode is chosen (before/with redirect).
sample({
  clock: playEpisodeClicked,
  target: close,
})

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: api.tvshows.$seriesPlaylist,
  key: 'tria__series-playlist',
  fail: noop,
})

sample({
  clock: initPlayerPageShowPlaying,
  source: {
    playlist: api.tvshows.$seriesPlaylist,
    requestKey: $seriesPlaylistRequestKey,
  },
  filter: ({ playlist, requestKey }, clockData) =>
    nay(playlist?.nextEpisode) &&
    requestKey !== getSeriesPlaylistKey(clockData),
  fn: (_, clockData) => {
    const params: IGenerateSeriesPlaylistParams = {
      tvShowId: clockData.tvShowId,
      seasonId: clockData.seasonId,
      episodeId: clockData.episodeId,
      categoryId: clockData.categoryId,
      showIndex: clockData.showIndex,
      isLooped: false, // default value
      isAutoplay: false, // default value
    }

    return params
  },
  target: api.tvshows.generateSeriesPlaylistFx,
})

sample({
  clock: initPlayerPageShowPlaying,
  source: {
    playlist: api.tvshows.$seriesPlaylist,
    requestKey: $seriesPlaylistRequestKey,
  },
  filter: ({ playlist, requestKey }, clockData) =>
    exists(playlist?.nextEpisode) &&
    requestKey !== getSeriesPlaylistKey(clockData),
  fn: ({ playlist }, clockData) => {
    const params: SeriesPlaylistRecalculate = {
      tvShowId: clockData.tvShowId,
      seasonId: clockData.seasonId,
      episodeId: clockData.episodeId,
      categoryId: clockData?.categoryId,
      showIndex: clockData.showIndex,
      isLooped: playlist?.isLooped,
      isAutoplay: playlist?.isAutoplay,
    }

    return params
  },
  target: api.tvshows.recalculateCurrentSeriesPlaylist,
})

sample({
  clock: api.tvshows.generateSeriesPlaylistFx.doneData,
  fn: ({ seasonsWithEpisodes, episodes }) => ({
    seasonsWithEpisodes,
    episodes,
  }),
  target: api.tvshows.$showPlayList,
})

export const $isLoading = api.tvshows.getSeasonsWithEpisodesFx.pending

export const $data = combine(
  api.tvshows.$showPlayList,
  $isLoading,
  api.tvshows.$seriesPlaylist,
  i18n.$t,
  (playlist, loading, seriesPlaylist, { t }) => {
    const temp = playlistFormatter({ playlist, t })

    return {
      ...temp,
      loading,
      currentEpisode: seriesPlaylist?.currentEpisode?.id,
      playEpisode: playEpisodeClicked,
    }
  }
)

export const $cwBeforeRedirect = createStore<ICWatching | null>(null)

export const $title = combine(
  showsModel.$currentShow,
  api.tvshows.$seriesPlaylist,
  (show, playlist) => {
    const entity = show || playlist?.currentTvShow || null
    return entity?.name || ''
  }
)
